import { useState } from 'react';
import { Zap, Tag, Clock, ChevronDown, ChevronUp, Pencil, Check } from 'lucide-react';
import { FOREST_GREEN, MID_GRAY, BORDER, SHADOWS, WHITE } from '@/styles/tokens';
import type { ProposedSkill } from '@/types/plan';

interface SkillPreviewCardProps {
  skill: ProposedSkill;
  onUpdate: (skill: ProposedSkill) => void;
  onCreateDraft: () => void;
  onCreateAndActivate: () => void;
  isCreating?: boolean;
}

export function SkillPreviewCard({
  skill,
  onUpdate,
  onCreateDraft,
  onCreateAndActivate,
  isCreating,
}: SkillPreviewCardProps) {
  const [instructionsExpanded, setInstructionsExpanded] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const startEdit = (field: string, value: string) => {
    setEditingField(field);
    setEditValue(value);
  };

  const commitEdit = (field: keyof ProposedSkill) => {
    onUpdate({ ...skill, [field]: editValue });
    setEditingField(null);
  };

  const domainColors: Record<string, string> = {
    data: '#3B82F6',
    workflow: '#8B5CF6',
    general: MID_GRAY.DEFAULT,
  };

  return (
    <div
      style={{
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: 8,
        background: WHITE,
        boxShadow: SHADOWS.sm,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: `${FOREST_GREEN.DEFAULT}08`,
          borderBottom: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Zap style={{ width: 16, height: 16, color: FOREST_GREEN.DEFAULT }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: FOREST_GREEN.DEFAULT }}>
          Skill Preview
        </span>
      </div>

      {/* Body */}
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Name */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Name
          </label>
          {editingField === 'name' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 14 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('name')}
              />
              <button onClick={() => commitEdit('name')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 500 }}>{skill.name}</span>
              <button
                onClick={() => startEdit('name', skill.name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
            Description
          </label>
          {editingField === 'description' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              <input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '4px 8px', borderRadius: 4, border: `1px solid ${BORDER.DEFAULT}`, fontSize: 13 }}
                onKeyDown={(e) => e.key === 'Enter' && commitEdit('description')}
              />
              <button onClick={() => commitEdit('description')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                <Check style={{ width: 14, height: 14, color: FOREST_GREEN.DEFAULT }} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 13, color: MID_GRAY.dark }}>{skill.description}</span>
              <button
                onClick={() => startEdit('description', skill.description)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.5 }}
              >
                <Pencil style={{ width: 12, height: 12 }} />
              </button>
            </div>
          )}
        </div>

        {/* Domain + Tags row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {skill.domain && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
                background: `${domainColors[skill.domain] || MID_GRAY.DEFAULT}15`,
                color: domainColors[skill.domain] || MID_GRAY.DEFAULT,
              }}
            >
              {skill.domain}
            </span>
          )}
          {skill.tags?.map((tag) => (
            <span
              key={tag}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                borderRadius: 10,
                background: `${MID_GRAY.DEFAULT}10`,
                color: MID_GRAY.DEFAULT,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Tag style={{ width: 10, height: 10 }} />
              {tag}
            </span>
          ))}
        </div>

        {/* Instructions (collapsible) */}
        <div>
          <button
            onClick={() => setInstructionsExpanded(!instructionsExpanded)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
              color: MID_GRAY.DEFAULT,
              textTransform: 'uppercase',
              padding: 0,
              marginBottom: 4,
            }}
          >
            Instructions
            {instructionsExpanded
              ? <ChevronUp style={{ width: 12, height: 12 }} />
              : <ChevronDown style={{ width: 12, height: 12 }} />
            }
          </button>
          {instructionsExpanded && (
            editingField === 'instructions' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <textarea
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                  rows={8}
                  style={{
                    width: '100%',
                    padding: 8,
                    borderRadius: 4,
                    border: `1px solid ${BORDER.DEFAULT}`,
                    fontSize: 12,
                    fontFamily: 'monospace',
                    resize: 'vertical',
                  }}
                />
                <button
                  onClick={() => commitEdit('instructions')}
                  style={{
                    alignSelf: 'flex-end',
                    padding: '4px 12px',
                    borderRadius: 4,
                    border: `1px solid ${FOREST_GREEN.DEFAULT}`,
                    background: FOREST_GREEN.DEFAULT,
                    color: WHITE,
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  Save
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <pre
                  style={{
                    fontSize: 12,
                    lineHeight: 1.5,
                    background: `${MID_GRAY.DEFAULT}06`,
                    padding: 10,
                    borderRadius: 4,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflow: 'auto',
                    margin: 0,
                  }}
                >
                  {skill.instructions}
                </pre>
                <button
                  onClick={() => startEdit('instructions', skill.instructions)}
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    background: WHITE,
                    border: `1px solid ${BORDER.DEFAULT}`,
                    borderRadius: 4,
                    cursor: 'pointer',
                    padding: 3,
                  }}
                >
                  <Pencil style={{ width: 12, height: 12, color: MID_GRAY.DEFAULT }} />
                </button>
              </div>
            )
          )}
        </div>

        {/* Triggers */}
        {skill.triggers && skill.triggers.length > 0 && (
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: MID_GRAY.DEFAULT, textTransform: 'uppercase', marginBottom: 4, display: 'block' }}>
              Triggers
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {skill.triggers.map((trigger, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 12,
                    color: MID_GRAY.dark,
                  }}
                >
                  <Clock style={{ width: 12, height: 12, color: MID_GRAY.DEFAULT }} />
                  <span style={{ fontWeight: 500 }}>{trigger.type}:</span>
                  <span>{JSON.stringify(trigger.config)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: '12px 16px',
          borderTop: `1px solid ${BORDER.DEFAULT}`,
          display: 'flex',
          gap: 8,
          justifyContent: 'flex-end',
        }}
      >
        <button
          onClick={onCreateDraft}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: `1px solid ${BORDER.DEFAULT}`,
            background: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          Create as Draft
        </button>
        <button
          onClick={onCreateAndActivate}
          disabled={isCreating}
          style={{
            padding: '6px 14px',
            borderRadius: 6,
            border: 'none',
            background: FOREST_GREEN.DEFAULT,
            color: WHITE,
            fontSize: 13,
            fontWeight: 500,
            cursor: isCreating ? 'not-allowed' : 'pointer',
            opacity: isCreating ? 0.6 : 1,
          }}
        >
          {isCreating ? 'Creating...' : 'Create & Activate'}
        </button>
      </div>
    </div>
  );
}
